/**
 * Voice Mode Hook for ElevenLabs Integration
 * Manages TTS (Text-to-Speech), STT (Speech-to-Text), audio recording, and time tracking
 * Limited to 5 minutes of total voice interaction per session
 * Uses the official ElevenLabs JavaScript SDK
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ElevenLabsClient } from '@elevenlabs/elevenlabs-js';

export type VoiceState = 'idle' | 'listening' | 'thinking' | 'speaking';

interface UseVoiceModeOptions {
    sessionId: string;
    onTranscription?: (text: string) => void;
    onError?: (error: string) => void;
}

interface UseVoiceModeReturn {
    voiceState: VoiceState;
    isVoiceModeEnabled: boolean;
    toggleVoiceMode: () => void;
    startListening: () => Promise<void>;
    stopListening: () => Promise<void>;
    speakText: (text: string, voiceId?: string) => Promise<void>;
    stopSpeaking: () => void;
    usedSeconds: number;
    remainingSeconds: number;
    isTimeLimitReached: boolean;
    audioLevel: number;
    error: string | null;
    clearError: () => void;
}

const VOICE_TIME_LIMIT_SECONDS = 5 * 60; // 5 minutes
const ELEVENLABS_API_KEY = import.meta.env.VITE_ELEVENLABS_API_KEY || '';

// Pool of voice IDs to assign to different agents
const AGENT_VOICE_IDS: Record<number, string> = {
    0: 'pqHfZKP75CvOlQylNhV4', // Voice 1
    1: 'pNInz6obpgDQGcFmaJgB', // Voice 2  
    2: 'Xb7hH8MSUJpSbSDYk0k2', // Voice 3
};

// Cache for agent voice assignments to ensure consistency
const agentVoiceCache: Record<string, string> = {};

// Get a consistent voice ID for an agent based on their ID
export function getVoiceIdForAgent(agentId: string): string {
    // Return cached voice if already assigned
    if (agentVoiceCache[agentId]) {
        return agentVoiceCache[agentId];
    }
    
    // Use a more robust hash function (djb2 algorithm)
    let hash = 5381;
    for (let i = 0; i < agentId.length; i++) {
        const char = agentId.charCodeAt(i);
        hash = ((hash << 5) + hash) + char; // hash * 33 + char
    }
    
    // Ensure positive number and map to voice index
    const voiceCount = Object.keys(AGENT_VOICE_IDS).length;
    const index = Math.abs(hash) % voiceCount;
    const voiceId = AGENT_VOICE_IDS[index];
    
    // Cache the assignment
    agentVoiceCache[agentId] = voiceId;
    
    console.log(`Assigned voice ${index} (${voiceId}) to agent: ${agentId}`);
    
    return voiceId;
}

export function useVoiceMode({ 
    sessionId, 
    onTranscription, 
    onError 
}: UseVoiceModeOptions): UseVoiceModeReturn {
    const [voiceState, setVoiceState] = useState<VoiceState>('idle');
    const [isVoiceModeEnabled, setIsVoiceModeEnabled] = useState(false);
    const [usedSeconds, setUsedSeconds] = useState(0);
    const [audioLevel, setAudioLevel] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const elevenLabsClientRef = useRef<ElevenLabsClient | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timeIntervalRef = useRef<number | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const recordingStartTimeRef = useRef<number | null>(null);
    const playbackStartTimeRef = useRef<number | null>(null);

    const storageKey = `voice_usage_${sessionId}`;

    // Initialize ElevenLabs client
    useEffect(() => {
        if (ELEVENLABS_API_KEY) {
            elevenLabsClientRef.current = new ElevenLabsClient({
                apiKey: ELEVENLABS_API_KEY,
            });
        }
    }, []);

    useEffect(() => {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed)) {
                setUsedSeconds(parsed);
            }
        }
    }, [storageKey]);

    useEffect(() => {
        sessionStorage.setItem(storageKey, usedSeconds.toString());
    }, [usedSeconds, storageKey]);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, []);

    const cleanup = useCallback(() => {
        if (timeIntervalRef.current) {
            clearInterval(timeIntervalRef.current);
            timeIntervalRef.current = null;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current.src = '';
            audioElementRef.current = null;
        }
        mediaRecorderRef.current = null;
        analyserRef.current = null;
    }, []);

    const remainingSeconds = Math.max(0, VOICE_TIME_LIMIT_SECONDS - usedSeconds);
    const isTimeLimitReached = usedSeconds >= VOICE_TIME_LIMIT_SECONDS;

    const handleError = useCallback((message: string) => {
        setError(message);
        onError?.(message);
        console.error('Voice mode error:', message);
    }, [onError]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const startTimeTracking = useCallback((type: 'recording' | 'playback') => {
        if (type === 'recording') {
            recordingStartTimeRef.current = Date.now();
        } else {
            playbackStartTimeRef.current = Date.now();
        }

        if (!timeIntervalRef.current) {
            timeIntervalRef.current = window.setInterval(() => {
                const now = Date.now();
                let additionalSeconds = 0;

                if (recordingStartTimeRef.current) {
                    additionalSeconds += Math.floor((now - recordingStartTimeRef.current) / 1000);
                }
                if (playbackStartTimeRef.current) {
                    additionalSeconds += Math.floor((now - playbackStartTimeRef.current) / 1000);
                }

                const totalUsed = usedSeconds + additionalSeconds;
                if (totalUsed >= VOICE_TIME_LIMIT_SECONDS) {
                    stopListening();
                    stopSpeaking();
                    setIsVoiceModeEnabled(false);
                    handleError('Voice time limit reached (5 minutes). Switching to text mode.');
                }
            }, 1000);
        }
    }, [usedSeconds, handleError]);

    const stopTimeTracking = useCallback((type: 'recording' | 'playback') => {
        const now = Date.now();

        if (type === 'recording' && recordingStartTimeRef.current) {
            const elapsed = Math.floor((now - recordingStartTimeRef.current) / 1000);
            setUsedSeconds(prev => prev + elapsed);
            recordingStartTimeRef.current = null;
        }

        if (type === 'playback' && playbackStartTimeRef.current) {
            const elapsed = Math.floor((now - playbackStartTimeRef.current) / 1000);
            setUsedSeconds(prev => prev + elapsed);
            playbackStartTimeRef.current = null;
        }

        if (!recordingStartTimeRef.current && !playbackStartTimeRef.current && timeIntervalRef.current) {
            clearInterval(timeIntervalRef.current);
            timeIntervalRef.current = null;
        }
    }, []);

    const updateAudioLevel = useCallback(() => {
        if (!analyserRef.current) {
            setAudioLevel(0);
            return;
        }

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalizedLevel = Math.min(1, average / 128);
        setAudioLevel(normalizedLevel);

        if (voiceState === 'listening' || voiceState === 'speaking') {
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
    }, [voiceState]);

    const toggleVoiceMode = useCallback(() => {
        if (isTimeLimitReached) {
            handleError('Voice time limit reached. Please use text input.');
            return;
        }

        if (!ELEVENLABS_API_KEY) {
            handleError('ElevenLabs API key not configured. Voice mode unavailable.');
            return;
        }

        setIsVoiceModeEnabled(prev => {
            if (prev) {
                cleanup();
                setVoiceState('idle');
            }
            return !prev;
        });
    }, [isTimeLimitReached, handleError, cleanup]);

    const startListening = useCallback(async () => {
        if (isTimeLimitReached) {
            handleError('Voice time limit reached.');
            return;
        }

        if (voiceState === 'listening') {
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 16000
                } 
            });
            streamRef.current = stream;

            audioContextRef.current = new AudioContext();
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 256;
            
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);

            const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
                ? 'audio/webm' 
                : 'audio/mp4';

            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                stopTimeTracking('recording');
                
                if (audioChunksRef.current.length === 0) {
                    setVoiceState('idle');
                    return;
                }

                setVoiceState('thinking');

                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                audioChunksRef.current = [];

                try {
                    const transcript = await transcribeAudio(audioBlob);
                    if (transcript && transcript.trim()) {
                        onTranscription?.(transcript);
                    }
                } catch (err) {
                    handleError('Failed to transcribe audio. Please try again.');
                } finally {
                    setVoiceState('idle');
                }
            };

            mediaRecorderRef.current.start(100);
            setVoiceState('listening');
            startTimeTracking('recording');
            updateAudioLevel();

        } catch (err) {
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError') {
                    handleError('Microphone permission denied. Please allow microphone access.');
                } else if (err.name === 'NotFoundError') {
                    handleError('No microphone found. Please connect a microphone.');
                } else {
                    handleError(`Failed to start recording: ${err.message}`);
                }
            }
        }
    }, [isTimeLimitReached, voiceState, handleError, startTimeTracking, stopTimeTracking, updateAudioLevel, onTranscription]);

    const stopListening = useCallback(async () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        setAudioLevel(0);
    }, []);

    const transcribeAudio = async (audioBlob: Blob): Promise<string> => {
        if (!elevenLabsClientRef.current) {
            throw new Error('ElevenLabs client not initialized');
        }

        // Convert blob to File for the SDK
        const audioFile = new File([audioBlob], 'recording.webm', { type: audioBlob.type });

        const result = await elevenLabsClientRef.current.speechToText.convert({
            file: audioFile,
            modelId: 'scribe_v1',
        });

        if ('text' in result && typeof result.text === 'string') {
            return result.text;
        }
        if ('channels' in result && Array.isArray(result.channels) && result.channels.length > 0) {
            const channel = result.channels[0] as { characters?: { text: string }[] };
            return channel?.characters?.map(c => c.text).join('') || '';
        }
        return '';
    };

    const speakText = useCallback(async (text: string, voiceId?: string) => {
        if (isTimeLimitReached) {
            return;
        }

        if (!text.trim()) {
            return;
        }

        if (!elevenLabsClientRef.current) {
            handleError('ElevenLabs client not initialized');
            return;
        }

        if (voiceState === 'speaking') {
            stopSpeaking();
        }

        setVoiceState('speaking');

        // Use provided voiceId or default to first voice in pool
        const selectedVoiceId = voiceId || AGENT_VOICE_IDS[0];

        try {
            // Use the ElevenLabs SDK for text-to-speech
            const audioResponse = await elevenLabsClientRef.current.textToSpeech.convert(
                selectedVoiceId,
                {
                    text,
                    modelId: 'eleven_multilingual_v2',
                    outputFormat: 'mp3_44100_128',
                }
            );

            // Handle the audio response - it could be a ReadableStream or similar
            let audioBlob: Blob;
            
            if (audioResponse instanceof Blob) {
                audioBlob = audioResponse;
            } else if (audioResponse instanceof ReadableStream) {
                // Convert the readable stream to a blob
                const chunks: ArrayBuffer[] = [];
                const reader = audioResponse.getReader();
                
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    if (value) {
                        // Copy to a new ArrayBuffer to avoid SharedArrayBuffer issues
                        const buffer = new ArrayBuffer(value.byteLength);
                        new Uint8Array(buffer).set(value);
                        chunks.push(buffer);
                    }
                }
                audioBlob = new Blob(chunks, { type: 'audio/mpeg' });
            } else if (typeof audioResponse === 'object' && audioResponse !== null) {
                // If it's an async iterable or has arrayBuffer method
                const response = audioResponse as Response;
                if (typeof response.blob === 'function') {
                    audioBlob = await response.blob();
                } else if (typeof response.arrayBuffer === 'function') {
                    const buffer = await response.arrayBuffer();
                    audioBlob = new Blob([buffer], { type: 'audio/mpeg' });
                } else {
                    // Try to iterate if it's an async iterable
                    const chunks: ArrayBuffer[] = [];
                    for await (const chunk of audioResponse as AsyncIterable<Uint8Array>) {
                        // Copy to a new ArrayBuffer to avoid SharedArrayBuffer issues
                        const buffer = new ArrayBuffer(chunk.byteLength);
                        new Uint8Array(buffer).set(chunk);
                        chunks.push(buffer);
                    }
                    audioBlob = new Blob(chunks, { type: 'audio/mpeg' });
                }
            } else {
                throw new Error('Unexpected audio response format');
            }
            const audioUrl = URL.createObjectURL(audioBlob);

            audioElementRef.current = new Audio(audioUrl);
            
            // Set up audio context for visualization
            audioContextRef.current = new AudioContext();
            const source = audioContextRef.current.createMediaElementSource(audioElementRef.current);
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 256;
            source.connect(analyserRef.current);
            analyserRef.current.connect(audioContextRef.current.destination);

            startTimeTracking('playback');
            updateAudioLevel();

            audioElementRef.current.onended = () => {
                stopTimeTracking('playback');
                setVoiceState('idle');
                setAudioLevel(0);
                URL.revokeObjectURL(audioUrl);
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
            };

            audioElementRef.current.onerror = () => {
                stopTimeTracking('playback');
                setVoiceState('idle');
                setAudioLevel(0);
                handleError('Failed to play audio response.');
            };

            await audioElementRef.current.play();

        } catch (err) {
            stopTimeTracking('playback');
            setVoiceState('idle');
            if (err instanceof Error) {
                handleError(`Text-to-speech failed: ${err.message}`);
            }
        }
    }, [isTimeLimitReached, voiceState, handleError, startTimeTracking, stopTimeTracking, updateAudioLevel]);

    const stopSpeaking = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current.currentTime = 0;
        }

        stopTimeTracking('playback');
        setVoiceState('idle');
        setAudioLevel(0);
    }, [stopTimeTracking]);

    return {
        voiceState,
        isVoiceModeEnabled,
        toggleVoiceMode,
        startListening,
        stopListening,
        speakText,
        stopSpeaking,
        usedSeconds,
        remainingSeconds,
        isTimeLimitReached,
        audioLevel,
        error,
        clearError,
    };
}

export default useVoiceMode;
